import Phaser from 'phaser';
import { ANIMAL_STATS } from '../config/animals';
import { makeMountWithRider } from '../render/mount';
import { DEFAULT_CHARACTER } from '../config/presets';
import type { AnimalKey } from '../types';

// Head-to-head RPS battle between two players. No AI. Each player picks
// secretly (using their own keys), then both reveal at the same time and the
// winner deals damage. First player to drop the other to 0 HP wins.

interface InitData {
  animalP1: AnimalKey;
  animalP2: AnimalKey;
}

type Move = 'attack' | 'defend' | 'heal';

const ATTACK_COLOR = 0xc23b3b;
const DEFEND_COLOR = 0x4a8acc;
const HEAL_COLOR = 0x3a8a4a;

const KEYS = {
  p1: { attack: 'A', defend: 'S', heal: 'D' },
  p2: { attack: 'J', defend: 'K', heal: 'L' },
} as const;

interface PlayerState {
  label: 'P1' | 'P2';
  hp: number;
  maxHp: number;
  pick: Move | null;
  hpBar: Phaser.GameObjects.Rectangle;
  hpText: Phaser.GameObjects.Text;
  pickText: Phaser.GameObjects.Text;
  sprite: Phaser.GameObjects.Container;
  pickIcon?: Phaser.GameObjects.Container;
}

export class TwoPlayerBossScene extends Phaser.Scene {
  private p1!: PlayerState;
  private p2!: PlayerState;
  private logText!: Phaser.GameObjects.Text;
  private over = false;
  private resolving = false;

  constructor() {
    super('TwoPlayerBossScene');
  }

  create(data: InitData): void {
    const W = this.scale.width;
    const H = this.scale.height;

    const bg = this.add.graphics();
    bg.fillGradientStyle(0x6b3aa8, 0x6b3aa8, 0x3d2914, 0x3d2914, 1);
    bg.fillRect(0, 0, W, H);

    this.add
      .text(W / 2, 40, '2-Player Battle', {
        fontFamily: 'sans-serif',
        fontSize: '32px',
        color: '#f2c14e',
      })
      .setOrigin(0.5);

    // Sprites — P1 on the left, P2 on the right.
    const p1Sprite = makeMountWithRider(this, data.animalP1, DEFAULT_CHARACTER, 280, 360);
    p1Sprite.setScale(2);
    const p2Sprite = makeMountWithRider(this, data.animalP2, DEFAULT_CHARACTER, W - 280, 360);
    p2Sprite.setScale(2);

    // HP bars
    this.p1 = this.makePlayer('P1', data.animalP1, p1Sprite, 40, 90, 1);
    this.p2 = this.makePlayer('P2', data.animalP2, p2Sprite, W - 40 - 360, 90, -1);

    this.logText = this.add
      .text(W / 2, H - 200, 'Both players pick a move secretly, then both reveal.', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffd9a0',
        align: 'center',
        wordWrap: { width: 760 },
      })
      .setOrigin(0.5);

    // Help line
    this.add
      .text(W / 2, H - 170, 'ATTACK beats HEAL  •  HEAL beats DEFEND  •  DEFEND beats ATTACK', {
        fontFamily: 'sans-serif',
        fontSize: '14px',
        color: '#bcd0e0',
      })
      .setOrigin(0.5);

    // Per-player legend
    this.makeLegend(W / 4, H - 110, 'P1 keys', KEYS.p1);
    this.makeLegend((W * 3) / 4, H - 110, 'P2 keys', KEYS.p2);

    // Quit
    const quit = this.add.container(W - 24, 18).setDepth(20);
    const quitBg = this.add
      .rectangle(0, 0, 90, 32, 0x3d2914, 0.85)
      .setStrokeStyle(2, 0xffffff)
      .setOrigin(1, 0);
    const quitTxt = this.add
      .text(-45, 16, 'QUIT', { fontFamily: 'sans-serif', fontSize: '14px', color: '#ffffff' })
      .setOrigin(0.5);
    quit.add([quitBg, quitTxt]);
    quitBg.setInteractive({ useHandCursor: true });
    quitBg.on('pointerdown', () => this.scene.start('TitleScene'));

    // Bind keys.
    const kb = this.input.keyboard!;
    kb.on('keydown-A', () => this.handlePick(this.p1, 'attack'));
    kb.on('keydown-S', () => this.handlePick(this.p1, 'defend'));
    kb.on('keydown-D', () => this.handlePick(this.p1, 'heal'));
    kb.on('keydown-J', () => this.handlePick(this.p2, 'attack'));
    kb.on('keydown-K', () => this.handlePick(this.p2, 'defend'));
    kb.on('keydown-L', () => this.handlePick(this.p2, 'heal'));

    // Touch buttons (3 per side)
    this.makeTouchRow(this.p1, 200, H - 60);
    this.makeTouchRow(this.p2, W - 200, H - 60);
  }

  private makePlayer(
    label: 'P1' | 'P2',
    animalKey: AnimalKey,
    sprite: Phaser.GameObjects.Container,
    barX: number,
    barY: number,
    iconSign: 1 | -1,
  ): PlayerState {
    const stats = ANIMAL_STATS[animalKey];
    const maxHp = stats.hp * 10 + 30;
    const barMax = 360;
    this.add.text(barX, barY, label, { fontFamily: 'sans-serif', fontSize: '18px', color: '#fff' });
    this.add
      .rectangle(barX, barY + 30, barMax, 20, 0x000000, 0.6)
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xffffff);
    const hpBar = this.add.rectangle(barX, barY + 30, barMax, 20, 0x4ea36b).setOrigin(0, 0);
    const hpText = this.add.text(barX, barY + 54, `HP: ${maxHp}/${maxHp}`, {
      fontFamily: 'sans-serif',
      fontSize: '14px',
      color: '#fff',
    });
    const pickText = this.add
      .text(sprite.x, sprite.y - 200, '— picking —', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffd9a0',
      })
      .setOrigin(0.5);

    return {
      label,
      hp: maxHp,
      maxHp,
      pick: null,
      hpBar,
      hpText,
      pickText,
      sprite,
      pickIcon: undefined,
    };
    // iconSign currently unused but reserved for icon position offset.
    void iconSign;
  }

  private makeLegend(
    cx: number,
    y: number,
    label: string,
    keys: { attack: string; defend: string; heal: string },
  ): void {
    this.add
      .text(cx, y - 18, label, {
        fontFamily: 'sans-serif',
        fontSize: '14px',
        color: '#bcd0e0',
      })
      .setOrigin(0.5);
    this.add
      .text(cx, y, `${keys.attack} = ⚔   ${keys.defend} = 🛡   ${keys.heal} = 💚`, {
        fontFamily: 'sans-serif',
        fontSize: '14px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
  }

  private makeTouchRow(p: PlayerState, cx: number, cy: number): void {
    const opts: Array<{ move: Move; label: string; color: number }> = [
      { move: 'attack', label: '⚔', color: ATTACK_COLOR },
      { move: 'defend', label: '🛡', color: DEFEND_COLOR },
      { move: 'heal', label: '💚', color: HEAL_COLOR },
    ];
    opts.forEach((opt, i) => {
      const x = cx + (i - 1) * 70;
      const c = this.add.container(x, cy).setDepth(15);
      const bg = this.add.rectangle(0, 0, 60, 60, opt.color).setStrokeStyle(2, 0xffffff);
      const t = this.add
        .text(0, 0, opt.label, { fontFamily: 'sans-serif', fontSize: '24px', color: '#fff' })
        .setOrigin(0.5);
      c.add([bg, t]);
      c.setSize(60, 60).setInteractive({ useHandCursor: true });
      c.on('pointerdown', () => this.handlePick(p, opt.move));
    });
  }

  private handlePick(p: PlayerState, move: Move): void {
    if (this.over || this.resolving) return;
    if (p.pick) return; // already picked this round
    p.pick = move;
    p.pickText.setText('✓ Locked in');
    p.pickText.setColor('#9aeaa8');

    if (this.p1.pick && this.p2.pick) this.resolveRound();
  }

  private resolveRound(): void {
    this.resolving = true;
    const p1 = this.p1.pick!;
    const p2 = this.p2.pick!;
    // Show icons.
    this.p1.pickIcon = this.makePickIcon(p1, this.p1.sprite.x, this.p1.sprite.y - 140);
    this.p2.pickIcon = this.makePickIcon(p2, this.p2.sprite.x, this.p2.sprite.y - 140);

    const damage = 12;
    const heal = 14;
    let log = '';

    if (p1 === p2) {
      // Tie cases
      if (p1 === 'attack') {
        const half = Math.round(damage / 2);
        this.p1.hp = Math.max(0, this.p1.hp - half);
        this.p2.hp = Math.max(0, this.p2.hp - half);
        log = `Both attacked! ${half} damage to each.`;
        this.flash(this.p1.sprite);
        this.flash(this.p2.sprite);
      } else if (p1 === 'defend') {
        log = 'Both defended. Nothing happens.';
      } else {
        const h = Math.round(heal / 2);
        this.p1.hp = Math.min(this.p1.maxHp, this.p1.hp + h);
        this.p2.hp = Math.min(this.p2.maxHp, this.p2.hp + h);
        log = `Both healed for ${h}.`;
      }
    } else if (beats(p1, p2)) {
      log = this.applyWin(this.p1, this.p2, p1, damage, heal);
    } else {
      log = this.applyWin(this.p2, this.p1, p2, damage, heal);
    }

    this.logText.setText(log);
    this.refreshHud();

    this.time.delayedCall(900, () => {
      this.p1.pickIcon?.destroy();
      this.p2.pickIcon?.destroy();
      this.p1.pickIcon = undefined;
      this.p2.pickIcon = undefined;

      if (this.p1.hp <= 0 && this.p2.hp <= 0) return this.declareWinner('Draw!');
      if (this.p1.hp <= 0) return this.declareWinner('P2 WINS!');
      if (this.p2.hp <= 0) return this.declareWinner('P1 WINS!');

      // Next round
      this.p1.pick = null;
      this.p2.pick = null;
      this.p1.pickText.setText('— picking —').setColor('#ffd9a0');
      this.p2.pickText.setText('— picking —').setColor('#ffd9a0');
      this.resolving = false;
    });
  }

  private applyWin(
    winner: PlayerState,
    loser: PlayerState,
    winMove: Move,
    damage: number,
    heal: number,
  ): string {
    if (winMove === 'attack') {
      loser.hp = Math.max(0, loser.hp - damage);
      this.flash(loser.sprite);
      return `${winner.label}'s attack landed for ${damage}!`;
    }
    if (winMove === 'defend') {
      return `${winner.label} blocked ${loser.label}'s attack.`;
    }
    // heal
    winner.hp = Math.min(winner.maxHp, winner.hp + heal);
    return `${winner.label} healed ${heal} HP while ${loser.label} defended.`;
  }

  private makePickIcon(move: Move, x: number, y: number): Phaser.GameObjects.Container {
    const c = this.add.container(x, y).setDepth(25);
    const color = move === 'attack' ? ATTACK_COLOR : move === 'defend' ? DEFEND_COLOR : HEAL_COLOR;
    const bg = this.add.circle(0, 0, 30, color).setStrokeStyle(3, 0xffffff);
    const txt = this.add
      .text(0, 0, iconFor(move), { fontFamily: 'sans-serif', fontSize: '32px', color: '#ffffff' })
      .setOrigin(0.5);
    c.add([bg, txt]);
    c.setScale(0);
    this.tweens.add({ targets: c, scale: 1, duration: 250, ease: 'Back.easeOut' });
    return c;
  }

  private flash(target: Phaser.GameObjects.Container): void {
    this.tweens.add({
      targets: target,
      alpha: 0.4,
      duration: 80,
      yoyo: true,
      repeat: 2,
    });
  }

  private refreshHud(): void {
    this.p1.hpBar.width = (this.p1.hp / this.p1.maxHp) * 360;
    this.p2.hpBar.width = (this.p2.hp / this.p2.maxHp) * 360;
    this.p1.hpText.setText(`HP: ${this.p1.hp}/${this.p1.maxHp}`);
    this.p2.hpText.setText(`HP: ${this.p2.hp}/${this.p2.maxHp}`);
  }

  private declareWinner(text: string): void {
    if (this.over) return;
    this.over = true;
    this.logText.setText(text);
    const W = this.scale.width;
    const H = this.scale.height;
    const overlay = this.add.container(W / 2, H / 2).setDepth(100);
    const dim = this.add.rectangle(0, 0, W, H, 0x000000, 0.6);
    const card = this.add.rectangle(0, 0, 520, 200, 0x6b3aa8).setStrokeStyle(4, 0xffffff);
    const title = this.add
      .text(0, -40, text, { fontFamily: 'sans-serif', fontSize: '36px', color: '#ffffff' })
      .setOrigin(0.5);
    const sub = this.add
      .text(0, 30, 'Tap to return to title', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffd9a0',
      })
      .setOrigin(0.5);
    overlay.add([dim, card, title, sub]);
    overlay.setSize(W, H).setInteractive();
    overlay.on('pointerdown', () => this.scene.start('TitleScene'));
    this.input.keyboard?.once('keydown', () => this.scene.start('TitleScene'));
  }
}

function beats(a: Move, b: Move): boolean {
  return (
    (a === 'attack' && b === 'heal') ||
    (a === 'heal' && b === 'defend') ||
    (a === 'defend' && b === 'attack')
  );
}

function iconFor(m: Move): string {
  return m === 'attack' ? '⚔' : m === 'defend' ? '🛡' : '💚';
}
